import { Flex, Tooltip, Typography, theme } from 'antd';
import { formatSecondsClock, formatSecondsRange } from '@/utils/format';

interface CoverageBarProps {
  /** Half-open `[lower, upper)` fractions of the whole, each in `0..1`. */
  ranges: { lower: number; upper: number }[];
  /** What the full bar is worth, in seconds. */
  totalSeconds: number;
  /** Extra line under the raw fraction in each segment's tooltip. */
  hint?: string;
  height?: number;
}

/**
 * Which parts of a product were consumed, drawn to scale: the track is the
 * whole product, each block one covered range. Hovering a block gives its
 * clock times and the raw fraction behind them.
 */
export const CoverageBar = ({
  ranges,
  totalSeconds,
  hint,
  height = 16,
}: CoverageBarProps) => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={token.marginXXS}>
      <div
        style={{
          position: 'relative',
          height,
          borderRadius: token.borderRadiusSM,
          background: token.colorFillSecondary,
          overflow: 'hidden',
        }}
      >
        {/* Painted to exact scale — no minimum width, or a session split into
            many short ranges would look far more covered than it is. */}
        {ranges.map((range) => (
          <div
            key={`fill-${range.lower}-${range.upper}`}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${range.lower * 100}%`,
              width: `${(range.upper - range.lower) * 100}%`,
              background: token.colorPrimary,
            }}
          />
        ))}

        {/* Hit areas sit on top, transparent and never narrower than a finger,
            so slivers stay hoverable without distorting the fill. */}
        {ranges.map((range) => (
          <Tooltip
            key={`hit-${range.lower}-${range.upper}`}
            title={
              <>
                <div>
                  {formatSecondsRange({
                    lower: Math.round(range.lower * totalSeconds),
                    upper: Math.round(range.upper * totalSeconds),
                  })}
                </div>
                <div>{`[${range.lower}, ${range.upper})`}</div>
                {hint && <div>{hint}</div>}
              </>
            }
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${range.lower * 100}%`,
                width: `${(range.upper - range.lower) * 100}%`,
                minWidth: 6,
                background: 'transparent',
              }}
            />
          </Tooltip>
        ))}
      </div>

      <Flex justify="space-between">
        <Typography.Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM }}
        >
          {formatSecondsClock(0)}
        </Typography.Text>
        <Typography.Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM }}
        >
          {formatSecondsClock(totalSeconds, { alwaysHours: true })}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};
